import React, { useState } from 'react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import ContactModal from './ContactModal';
import { Sheet, SheetContent } from './ui/sheet';
import { Menu, X } from 'lucide-react';

import './ProductHeader.css';

const linkClassName =
  'text-sm text-gray-600 hover:text-gray-900 transition-colors duration-300 hover:-translate-y-0.5 inline-block';

const actionStyles = {
  default:
    'text-sm bg-gray-900 text-white hover:bg-gray-800 transition-all duration-300 rounded-lg shadow-none',
  outline:
    'text-sm border-gray-200 hover:bg-gray-200 transition-all duration-300 rounded-lg',
  blackOutline:
    'text-sm border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-all duration-300 rounded-lg',
};

export default function ProductHeader({
  scrolled,
  isWrityPage = false,
  alwaysBorder = false,
  rightBrand,
  links,
  action,
  highlightedAction,
}) {
  const navigate = useNavigate();
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const showBorder = Boolean(scrolled) || alwaysBorder;
  const resolvedLinks = links ?? (
    isWrityPage
      ? [{ label: 'Back to Home', to: '/' }]
      : [{ label: 'Other Products', href: '/#products' }]
  );
  const resolvedAction = action ?? {
    label: 'Contact',
    variant: 'blackOutline',
    onClick: () => setIsContactOpen(true),
  };
  const resolvedHighlightedAction = highlightedAction ?? null;

  const scrollToTop = () => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const handleItemClick = (item) => {
    if (typeof item?.onClick === 'function') {
      item.onClick();
      return;
    }

    if (item?.to) {
      navigate(item.to);
      requestAnimationFrame(scrollToTop);
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-white/80 backdrop-blur-xl' : 'bg-transparent'
        } ${showBorder ? 'border-b border-gray-200/50' : ''}`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
        <div 
            className="logo cursor-pointer flex items-center gap-3" 
            onClick={() => navigate('/')}
          >
            {/* Logo container */}
            <div className="flex items-center justify-center">
              <img
                src="/Logo.svg"
                alt="Longfloat Logo"
                className="w-8 h-8 object-contain"
              />
            </div>
            <span className="text-2xl font-light tracking-tight text-gray-900">Longfloat</span>
          </div>
          
          <nav className="bp-desktop-nav items-center gap-8">
            {rightBrand && (rightBrand.logoSrc || rightBrand.title) && (
              <button
                type="button"
                onClick={() => handleItemClick(rightBrand)}
                className={`flex items-center gap-3 shrink-0 bg-transparent border-0 p-0 ${
                  rightBrand.to || rightBrand.onClick ? 'cursor-pointer' : 'cursor-default'
                }`}
              >
                {rightBrand.logoSrc && (
                  <img
                    src={rightBrand.logoSrc}
                    alt={rightBrand.alt || rightBrand.title || 'Product logo'}
                    className={rightBrand.logoClassName || 'h-8 w-auto object-contain'}
                  />
                )}
                {rightBrand.title && (
                  <span className="text-sm font-medium tracking-tight text-gray-900">
                    {rightBrand.title}
                  </span>
                )}
              </button>
            )}

            {resolvedLinks.map((link, index) => {
              if (link.href) {
                return (
                  <a
                    key={`${link.label}-${index}`}
                    href={link.href}
                    target={link.target}
                    rel={link.rel}
                    className={linkClassName}
                    onClick={link.onClick}
                  >
                    {link.label}
                  </a>
                );
              }

              return (
                <button
                  key={`${link.label}-${index}`}
                  type="button"
                  onClick={() => handleItemClick(link)}
                  className={`${linkClassName} cursor-pointer bg-transparent border-0 p-0`}
                >
                  {link.label}
                </button>
              );
            })}

            {(resolvedAction || resolvedHighlightedAction) && (
              <div className="flex items-center gap-3">
                {resolvedAction && (
                  <Button
                    variant={resolvedAction.variant || 'outline'}
                    className={actionStyles[resolvedAction.variant || 'blackOutline'] || actionStyles.blackOutline}
                    onClick={resolvedAction.onClick}
                  >
                    {resolvedAction.label}
                  </Button>
                )}

                {resolvedHighlightedAction && (
                  <Button
                    variant={resolvedHighlightedAction.variant || 'default'}
                    className={
                      actionStyles[resolvedHighlightedAction.variant || 'default'] || actionStyles.default
                    }
                    onClick={resolvedHighlightedAction.onClick}
                  >
                    {resolvedHighlightedAction.label}
                  </Button>
                )}
              </div>
            )}
          </nav>

          <button
            type="button"
            className="bp-mobile-toggle productHeaderHamburgerButton"
            aria-label="Open menu"
            onClick={() => setMobileMenuOpen((v) => !v)}
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right">
          <div className="flex flex-col gap-4">
            {rightBrand && (rightBrand.logoSrc || rightBrand.title) && (
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleItemClick(rightBrand);
                }}
                className="flex items-center gap-3 shrink-0 bg-transparent border-0 p-0 cursor-pointer"
              >
                {rightBrand.logoSrc && (
                  <img
                    src={rightBrand.logoSrc}
                    alt={rightBrand.alt || rightBrand.title || 'Product logo'}
                    className={rightBrand.logoClassName || 'h-8 w-auto object-contain'}
                  />
                )}
                {rightBrand.title && (
                  <span className="text-sm font-medium tracking-tight text-gray-900">
                    {rightBrand.title}
                  </span>
                )}
              </button>
            )}

            {resolvedLinks.map((link, index) => {
              if (link.href) {
                return (
                  <a
                    key={`${link.label}-${index}`}
                    href={link.href}
                    target={link.target}
                    rel={link.rel}
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-300"
                    onClick={(e) => {
                      if (typeof link.onClick === 'function') link.onClick(e);
                      setMobileMenuOpen(false);
                    }}
                  >
                    {link.label}
                  </a>
                );
              }

              return (
                <button
                  key={`${link.label}-${index}`}
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleItemClick(link);
                  }}
                  className="text-left text-sm text-gray-600 hover:text-gray-900 transition-colors duration-300"
                >
                  {link.label}
                </button>
              );
            })}

            {(resolvedAction || resolvedHighlightedAction) && (
              <div className="pt-2 mt-2 border-t border-slate-200/70 flex flex-col gap-3">
                {resolvedAction && (
                  <Button
                    variant={resolvedAction.variant || 'outline'}
                    className={actionStyles[resolvedAction.variant || 'blackOutline'] || actionStyles.blackOutline}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      resolvedAction.onClick?.();
                    }}
                  >
                    {resolvedAction.label}
                  </Button>
                )}

                {resolvedHighlightedAction && (
                  <Button
                    variant={resolvedHighlightedAction.variant || 'default'}
                    className={
                      actionStyles[resolvedHighlightedAction.variant || 'default'] || actionStyles.default
                    }
                    onClick={() => {
                      setMobileMenuOpen(false);
                      resolvedHighlightedAction.onClick?.();
                    }}
                  >
                    {resolvedHighlightedAction.label}
                  </Button>
                )}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <ContactModal 
        isOpen={isContactOpen} 
        onClose={() => setIsContactOpen(false)} 
      />
    </>
  );
}
